import { useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { FaBalanceScale, FaPlus, FaTimes } from "react-icons/fa";
import http from "../../api/apiClient";
import { euro, monthLabel, type Receipt } from "./contributionsModel";
import { centsToInput, toCents } from "./SplitEditor";

type Row = { key: number; childId: string; year: number | null; month: string; amount: string };

/** Calendar year in which the school year (August–July) of a month (YYYY-MM) or date starts. */
const yearStartOfMonth = (month: string): number | null => {
  const [year, m] = month.split("-").map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(m)) return null;
  return m >= 8 ? year : year - 1;
};

const nextMonth = (month: string): string => {
  const [year, m] = month.split("-").map(Number);
  const date = new Date(year, m, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

/**
 * Spreads one payment by hand over months of the family's children. Nothing is moved automatically: what is not
 * in a line stays not divided.
 */
export default function TermPartsEditor({ receipt, childOptions, months, onDone, onCancel }: {
  receipt: Receipt;
  childOptions: { childId: string; name: string }[];
  /** Months that may be chosen: the current, the previous and the next school year. */
  months: string[];
  onDone: () => void;
  onCancel: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const nextKey = useRef(0);
  // School years to choose from, and the one shown first: that of the payment (or else the middle one).
  const years = Array.from(new Set(months.map(yearStartOfMonth).filter((year): year is number => year !== null))).sort();
  const paymentYear = receipt.date ? yearStartOfMonth(String(receipt.date).slice(0, 7)) : null;
  const defaultYear = paymentYear !== null && years.includes(paymentYear) ? paymentYear : years[Math.floor(years.length / 2)] ?? null;
  const newRow = (childId = "", month = "", amount = "", year: number | null = null): Row => ({
    key: nextKey.current++, childId, month, amount, year: month ? yearStartOfMonth(month) : year ?? defaultYear,
  });
  const [rows, setRows] = useState<Row[]>(() => receipt.termParts?.length
    ? receipt.termParts.map((part) => newRow(part.childId, part.termMonth, centsToInput(Math.round(Number(part.amount) * 100))))
    : [newRow()]);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const total = Math.round(Number(receipt.amount) * 100);
  const placed = rows.reduce((sum, row) => sum + (toCents(row.amount) ?? 0), 0);
  const notDivided = total - placed;
  // Lines with nothing filled in are left out.
  const filled = rows.filter((row) => row.childId || row.month || row.amount.trim());
  const incomplete = filled.some((row) => !row.childId || !row.month || (toCents(row.amount) ?? 0) <= 0);
  const keys = filled.map((row) => `${row.childId}|${row.month}`);
  const duplicate = new Set(keys).size !== keys.length;
  const valid = filled.length > 0 && !incomplete && !duplicate && notDivided >= 0 && !saving;

  const update = (key: number, changes: Partial<Row>) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...changes } : row)));

  /** Another school year keeps the same month (September stays September), when that month can be chosen. */
  const changeYear = (row: Row, year: number) => {
    let month = "";
    if (row.month) {
      const [, m] = row.month.split("-").map(Number);
      const candidate = `${m >= 8 ? year : year + 1}-${String(m).padStart(2, "0")}`;
      if (months.includes(candidate)) month = candidate;
    }
    update(row.key, { year, month });
  };

  // Lines with a child and a month: the terms the payment can be divided over evenly.
  const chosen = rows.filter((row) => row.childId && row.month);

  /** The whole payment evenly over the chosen terms; leftover cents go one each to the first lines. */
  const divideEvenly = () => {
    if (chosen.length === 0) return;
    const share = Math.floor(total / chosen.length);
    const leftover = total - share * chosen.length;
    const amounts = new Map(chosen.map((row, index) => [row.key, centsToInput(share + (index < leftover ? 1 : 0))]));
    setRows((current) => current.map((row) => (amounts.has(row.key) ? { ...row, amount: amounts.get(row.key)! } : row)));
  };

  /** A new line continues the previous one: same child and amount, the next month. */
  const addRow = () => setRows((current) => {
    const last = current[current.length - 1];
    if (!last || !last.childId) return [...current, newRow("", "", "", last?.year ?? null)];
    const following = last.month ? nextMonth(last.month) : "";
    return [...current, newRow(last.childId, months.includes(following) ? following : "", last.amount, last.year)];
  });

  const errorText = (code?: string) => {
    switch (code) {
      case "TERM_PARTS_EXCEED_AMOUNT": return t("finance.payments.parts.errors.exceed", { defaultValue: "The parts add up to more than the payment." });
      case "TERM_OUTSIDE_WINDOW": return t("finance.payments.parts.errors.window", { defaultValue: "That month is outside the school years you can choose." });
      case "TERM_SCHOOL_YEAR_MISSING": return t("finance.payments.parts.errors.school_year", { defaultValue: "There is no school year for that month yet." });
      case "TERM_PART_CHILD_NOT_IN_FAMILY": return t("finance.payments.parts.errors.child", { defaultValue: "That child does not belong to the family of the payment." });
      case "TERM_PART_AMOUNT_INVALID": return t("finance.payments.parts.errors.amount", { defaultValue: "Enter an amount above zero per part, with at most two decimals." });
      case "TERM_PARTS_NEED_FAMILY": return t("finance.payments.parts.errors.linked", { defaultValue: "A payment must be linked to a family to be divided." });
      case "TERM_PART_DUPLICATE": return t("finance.payments.parts.duplicate", { defaultValue: "A child can get only one part for a month." });
      default: return t("finance.payments.save_failed", { defaultValue: "Saving failed. Please try again." });
    }
  };

  const save = async () => {
    if (!valid) return;
    setSaving(true);
    setMessage(null);
    try {
      await http.put(`/finance/receipts/${receipt.id}/term-parts`, {
        parts: filled.map((row) => ({
          childId: row.childId,
          termMonth: row.month,
          amount: ((toCents(row.amount) ?? 0) / 100).toFixed(2),
        })),
      });
      onDone();
    } catch (caught: any) {
      setMessage(errorText(caught?.response?.data?.message));
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (saving) return;
    setSaving(true);
    setMessage(null);
    try {
      await http.delete(`/finance/receipts/${receipt.id}/term-parts`);
      onDone();
    } catch {
      setMessage(t("finance.payments.save_failed", { defaultValue: "Saving failed. Please try again." }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-2 rounded-xl border border-indigo-200 bg-indigo-50/50 p-3 text-sm dark:border-indigo-400/20 dark:bg-indigo-400/5">
      <p className="font-bold text-slate-900 dark:text-white">
        {t("finance.payments.parts.title", { amount: euro(receipt.amount), defaultValue: "Divide {{amount}} over months" })}
      </p>
      <p className="text-xs text-slate-500">
        {t("finance.payments.parts.intro", { defaultValue: "Pick a child and a month per line. What you do not divide stays not divided; nothing is moved automatically." })}
      </p>
      {rows.map((row, index) => (
        <div key={row.key} data-part={index} className="flex flex-wrap items-center gap-2">
          <select
            value={row.childId}
            onChange={(event) => update(row.key, { childId: event.target.value })}
            aria-label={t("finance.payments.parts.child", { number: index + 1, defaultValue: "Child for part {{number}}" })}
            className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm dark:border-white/10 dark:bg-slate-900"
          >
            <option value="">{t("finance.payments.parts.choose_child", { defaultValue: "Choose child" })}</option>
            {childOptions.map((child) => <option key={child.childId} value={child.childId}>{child.name}</option>)}
          </select>
          {years.length > 1 && (
            <select
              value={row.year ?? ""}
              onChange={(event) => changeYear(row, Number(event.target.value))}
              aria-label={t("finance.payments.parts.school_year", { number: index + 1, defaultValue: "School year for part {{number}}" })}
              className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm tabular-nums dark:border-white/10 dark:bg-slate-900"
            >
              {years.map((year) => <option key={year} value={year}>{`${year}–${year + 1}`}</option>)}
            </select>
          )}
          <select
            value={row.month}
            onChange={(event) => update(row.key, { month: event.target.value })}
            aria-label={t("finance.payments.parts.month", { number: index + 1, defaultValue: "Month for part {{number}}" })}
            className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm dark:border-white/10 dark:bg-slate-900"
          >
            <option value="">{t("finance.payments.parts.choose_month", { defaultValue: "Choose month" })}</option>
            {months.filter((month) => years.length <= 1 || yearStartOfMonth(month) === row.year)
              .map((month) => <option key={month} value={month}>{monthLabel(month, "long")}</option>)}
          </select>
          <input
            inputMode="decimal"
            value={row.amount}
            placeholder="0,00"
            onChange={(event) => update(row.key, { amount: event.target.value })}
            aria-label={t("finance.payments.parts.amount", { number: index + 1, defaultValue: "Amount for part {{number}}" })}
            className="h-9 w-24 rounded-lg border border-slate-200 bg-white px-2 text-right text-sm tabular-nums dark:border-white/10 dark:bg-slate-900"
          />
          {rows.length > 1 && (
            <button
              type="button"
              onClick={() => setRows((current) => current.filter((other) => other.key !== row.key))}
              aria-label={t("finance.payments.parts.remove", { number: index + 1, defaultValue: "Remove part {{number}}" })}
              className="rounded-md p-1.5 text-slate-500 hover:bg-white dark:hover:bg-white/10"
            >
              <FaTimes aria-hidden="true" />
            </button>
          )}
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={addRow}
          className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:underline dark:text-indigo-300"
        >
          <FaPlus aria-hidden="true" />
          {t("finance.payments.parts.add", { defaultValue: "Add a part" })}
        </button>
        <button
          type="button"
          onClick={divideEvenly}
          disabled={chosen.length === 0 || saving}
          title={chosen.length === 0 ? t("finance.payments.parts.divide_evenly_help", { defaultValue: "Choose a child and a month on the lines first." }) : undefined}
          className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline dark:text-indigo-300"
        >
          <FaBalanceScale aria-hidden="true" />
          {t("finance.payments.parts.divide_evenly", { count: chosen.length, defaultValue: "Divide evenly over the chosen terms" })}
        </button>
        <span role="status" className={`text-xs font-semibold tabular-nums ${notDivided === 0 ? "text-emerald-700 dark:text-emerald-300" : "text-amber-700 dark:text-amber-300"}`}>
          {notDivided === 0
            ? t("finance.payments.split.complete", { defaultValue: "Fully divided" })
            : notDivided > 0
              ? t("finance.payments.parts.remaining", { amount: euro(notDivided / 100), defaultValue: "{{amount}} not divided" })
              : t("finance.payments.parts.too_much", { amount: euro(-notDivided / 100), defaultValue: "{{amount}} too much" })}
        </span>
      </div>
      {incomplete && (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          {t("finance.payments.parts.incomplete", { defaultValue: "Fill in a child, a month and an amount on every line, or remove the line." })}
        </p>
      )}
      {duplicate && (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          {t("finance.payments.parts.duplicate", { defaultValue: "A child can get only one part for a month." })}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={save}
          disabled={!valid}
          className="inline-flex h-9 items-center rounded-lg bg-indigo-600 px-3 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-40"
        >
          {t("finance.payments.parts.save", { defaultValue: "Save division" })}
        </button>
        {receipt.termParts && receipt.termParts.length > 0 && (
          <button
            type="button"
            onClick={clear}
            disabled={saving}
            className="inline-flex h-9 items-center rounded-lg px-3 text-sm font-semibold text-rose-600 hover:bg-white disabled:opacity-40 dark:hover:bg-white/10"
          >
            {t("finance.payments.parts.clear", { defaultValue: "Remove division" })}
          </button>
        )}
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex h-9 items-center rounded-lg border border-slate-200 px-3 text-sm font-semibold text-slate-600 hover:bg-white dark:border-white/10 dark:text-slate-300"
        >
          {t("common.cancel", { defaultValue: "Cancel" })}
        </button>
      </div>
      {message && <p role="alert" className="text-xs font-semibold text-rose-600">{message}</p>}
    </div>
  );
}
